import { Title } from '@common/components';
import useFormatDate from '@common/hooks/useFormatDate';
import {
  getHoursFromSeconds,
  getMinutesFromSeconds,
} from '@common/util/datetime';
import { formatDecimal } from '@common/util/math';
import { IdentificationStatsFactory } from '@dashboard/components/IdentificationStatsFactory';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import classes from './LevelOfImprovementObtainedStats.module.scss';

export default function LevelOfImprovementObtainedStats({
  stats,
  isLoading,
  groupBy,
}) {
  const { t } = useTranslation('dashboard\\IdentificationStatsView');

  const formatDate = useFormatDate();

  const getCourseTitle = () => t('levelOfImprovementObtainedCourseTitle');

  const levelMap = {
    LOW: t('levelLow'),
    MEDIUM: t('levelMedium'),
    HIGH: t('levelHigh'),
  };

  const headerColumnMap = {
    firstEvaluationAverage: (group, courseRow) => ({
      key: `${group}FirstEvaluationAverage`,
      content: (
        <>
          {t('firstEvaluationAverage')}
          <br />(
          {courseRow[group].firstEvaluationDate
            ? formatDate(courseRow[group].firstEvaluationDate)
            : '-'}
          )
        </>
      ),
    }),
    lastEvaluationAverage: (group, courseRow) => ({
      key: `${group}LastEvaluationAverage`,
      content: (
        <>
          {t('lastEvaluationAverage')}
          <br />(
          {courseRow[group].lastEvaluationDate
            ? formatDate(courseRow[group].lastEvaluationDate)
            : '-'}
          )
        </>
      ),
    }),
    levelOfImprovementObtained: (group) => ({
      key: `${group}LevelOfImprovementObtained`,
      content: t('levelOfImprovementObtained'),
      className: classes.bgBlue,
    }),
    workedTimeAverage: (group) => ({
      key: `${group}WorkedTimeAverage`,
      content: t('workedTimeAverage'),
    }),
    improvementPerHourOfWork: (group) => ({
      key: `${group}ImprovementPerHourOfWork`,
      content: t('improvementPerHourOfWork'),
    }),
    competencyLevel: (group) => ({
      key: `${group}CompetencyLevel`,
      content: t('competencyLevel'),
    }),
    pooledStandardDeviation: (group) => ({
      key: `${group}PooledStandardDeviation`,
      content: t('pooledStandardDeviation'),
      className: classes.leftSeparator,
    }),
    effectSize: (group) => ({
      key: `${group}EffectSize`,
      content: (
        <>
          {t('effectSize')}
          <br />
          <Title type="span" color="grey-400">
            {t('effectSizeHint')}
          </Title>
        </>
      ),
      className: classes.bgGreen,
    }),
  };

  const bodyColumnMap = {
    firstEvaluationAverage: (statRow, group) => ({
      key: `${group}FirstEvaluationAverage`,
      content: `${formatDecimal(statRow[group].firstEvaluationAverage)}%`,
    }),
    lastEvaluationAverage: (statRow, group) => ({
      key: `${group}LastEvaluationAverage`,
      content: `${formatDecimal(statRow[group].lastEvaluationAverage)}%`,
    }),
    levelOfImprovementObtained: (statRow, group) => ({
      key: `${group}LevelOfImprovementObtained`,
      content: `${formatDecimal(statRow[group].levelOfImprovementObtained)}%`,
      className: classes.bgBlue,
    }),
    workedTimeAverage: (statRow, group) => ({
      key: `${group}WorkedTimeAverage`,
      content: `${getHoursFromSeconds(
        statRow[group].workedTimeAverage,
      )}h. ${getMinutesFromSeconds(statRow[group].workedTimeAverage)}m.`,
    }),
    improvementPerHourOfWork: (statRow, group) => ({
      key: `${group}ImprovementPerHourOfWork`,
      content: `${formatDecimal(statRow[group].improvementPerHourOfWork)}%`,
    }),
    competencyLevel: (statRow, group) => ({
      key: `${group}CompetencyLevel`,
      content: statRow[group].competencyLevel
        ? levelMap[statRow[group].competencyLevel]
        : '-',
      className: classes.uppercase,
    }),
    pooledStandardDeviation: (statRow, group) => ({
      key: `${group}PooledStandardDeviation`,
      content: formatDecimal(statRow[group].pooledStandardDeviation) || '-',
      className: classes.leftSeparator,
    }),
    effectSize: (statRow, group) => ({
      key: `${group}EffectSize`,
      content: formatDecimal(statRow[group].effectSize) || '-',
      className: classes.bgGreen,
    }),
  };

  const renderGroupStats = ({ courseRow, renderStatsTable, group }) => {
    const headerColumns = [
      headerColumnMap.firstEvaluationAverage(group, courseRow),
      headerColumnMap.lastEvaluationAverage(group, courseRow),
      headerColumnMap.levelOfImprovementObtained(group),
    ];

    if (group === 'workingGroup') {
      headerColumns.push(
        headerColumnMap.workedTimeAverage(group),
        headerColumnMap.improvementPerHourOfWork(group),
      );
    }

    headerColumns.push(
      headerColumnMap.competencyLevel(group),
      headerColumnMap.pooledStandardDeviation(group),
      headerColumnMap.effectSize(group),
    );

    const getBodyColumns = ({ statRow }) => {
      const columns = [
        bodyColumnMap.firstEvaluationAverage(statRow, group),
        bodyColumnMap.lastEvaluationAverage(statRow, group),
        bodyColumnMap.levelOfImprovementObtained(statRow, group),
      ];

      if (group === 'workingGroup') {
        columns.push(
          bodyColumnMap.workedTimeAverage(statRow, group),
          bodyColumnMap.improvementPerHourOfWork(statRow, group),
        );
      }

      columns.push(
        bodyColumnMap.competencyLevel(statRow, group),
        bodyColumnMap.pooledStandardDeviation(statRow, group),
        bodyColumnMap.effectSize(statRow, group),
      );

      return columns;
    };

    return (
      <>
        <Title type="h4" className={classes.statsTitle}>
          {t(
            group === 'workingGroup'
              ? 'levelOfImprovementObtainedWorkingGroup'
              : 'levelOfImprovementObtainedControlGroup',
          )}
        </Title>
        {renderStatsTable({ courseRow, headerColumns, getBodyColumns })}
      </>
    );
  };

  const renderBothGroupsStats = ({ courseRow, renderStatsTable }) => {
    const topHeaderColumnContent = t('evaluation');

    const topHeaderColumns = [
      {
        key: 'workingGroup1',
        colspan: 4,
        content: t('workingGroup'),
      },
      {
        key: 'controlGroup1',
        colspan: 4,
        content: t('controlGroup'),
      },
      {
        key: 'workingGroup2',
        colspan: 2,
        content: t('workingGroup'),
        className: classes.leftSeparator,
      },
      {
        key: 'controlGroup2',
        colspan: 2,
        content: t('controlGroup'),
        className: classes.leftSeparator,
      },
    ];

    const headerColumns = [
      headerColumnMap.firstEvaluationAverage('workingGroup', courseRow),
      headerColumnMap.lastEvaluationAverage('workingGroup', courseRow),
      headerColumnMap.levelOfImprovementObtained('workingGroup'),
      headerColumnMap.competencyLevel('workingGroup'),
      headerColumnMap.firstEvaluationAverage('controlGroup', courseRow),
      headerColumnMap.lastEvaluationAverage('controlGroup', courseRow),
      headerColumnMap.levelOfImprovementObtained('controlGroup'),
      headerColumnMap.competencyLevel('controlGroup'),
      headerColumnMap.pooledStandardDeviation('workingGroup'),
      headerColumnMap.effectSize('workingGroup'),
      headerColumnMap.pooledStandardDeviation('controlGroup'),
      headerColumnMap.effectSize('controlGroup'),
    ];

    const getBodyColumns = ({ statRow }) => {
      return [
        bodyColumnMap.firstEvaluationAverage(statRow, 'workingGroup'),
        bodyColumnMap.lastEvaluationAverage(statRow, 'workingGroup'),
        bodyColumnMap.levelOfImprovementObtained(statRow, 'workingGroup'),
        bodyColumnMap.competencyLevel(statRow, 'workingGroup'),
        bodyColumnMap.firstEvaluationAverage(statRow, 'controlGroup'),
        bodyColumnMap.lastEvaluationAverage(statRow, 'controlGroup'),
        bodyColumnMap.levelOfImprovementObtained(statRow, 'controlGroup'),
        bodyColumnMap.competencyLevel(statRow, 'controlGroup'),
        bodyColumnMap.pooledStandardDeviation(statRow, 'workingGroup'),
        bodyColumnMap.effectSize(statRow, 'workingGroup'),
        bodyColumnMap.pooledStandardDeviation(statRow, 'controlGroup'),
        bodyColumnMap.effectSize(statRow, 'controlGroup'),
      ];
    };

    return (
      <>
        <Title type="h4" className={classes.statsTitle}>
          {t('levelOfImprovementObtainedGroupComparison')}
        </Title>
        {renderStatsTable({
          courseRow,
          headerColumns,
          getBodyColumns,
          topHeaderColumnContent,
          topHeaderColumns,
        })}
      </>
    );
  };

  const renderGroupComparisonStats = ({ courseRow, renderStatsTable }) => {
    const headerColumns = [
      {
        key: 'controlGroupLastEvaluationAverage',
        content: t('controlGroupLastEvaluationAverage'),
      },
      {
        key: 'workingGroupLastEvaluationAverage',
        content: t('workingGroupLastEvaluationAverage'),
      },
      {
        key: 'differenceInImprovementObtained',
        content: t('differenceInImprovementObtained'),
        className: classes.bgBlue,
      },
      {
        key: 'pooledStandardDeviation',
        content: t('pooledStandardDeviation'),
        className: classes.leftSeparator,
      },
      {
        key: 'effectSize',
        content: (
          <>
            {t('effectSize')}
            <br />
            <Title type="span" color="grey-400">
              {t('effectSizeHint')}
            </Title>
          </>
        ),
        className: classes.bgGreen,
      },
    ];

    const getBodyColumns = ({ statRow }) => {
      return [
        {
          key: 'controlGroupLastEvaluationAverage',
          content: `${formatDecimal(
            statRow.groupComparison.controlGroupLastEvaluationAverage,
          )}%`,
        },
        {
          key: 'workingGroupLastEvaluationAverage',
          content: `${formatDecimal(
            statRow.groupComparison.workingGroupLastEvaluationAverage,
          )}%`,
        },
        {
          key: 'differenceInImprovementObtained',
          content: `${formatDecimal(
            statRow.groupComparison.differenceInImprovementObtained,
          )}%`,
          className: classes.bgBlue,
        },
        {
          key: 'pooledStandardDeviation',
          content:
            formatDecimal(statRow.groupComparison.pooledStandardDeviation) ||
            '-',
          className: classes.leftSeparator,
        },
        {
          key: 'effectSize',
          content: formatDecimal(statRow.groupComparison.effectSize) || '-',
          className: classes.bgGreen,
        },
      ];
    };

    return (
      <>
        <Title type="h4" className={classes.statsTitle}>
          {t('levelOfImprovementObtainedGroupFinalComparison')}
        </Title>
        {renderStatsTable({
          courseRow,
          headerColumns,
          getBodyColumns,
        })}
      </>
    );
  };

  const renderCourseStats = ({ courseRow, renderStatsTable }) => {
    return (
      <>
        {renderGroupStats({
          courseRow,
          renderStatsTable,
          group: 'workingGroup',
        })}
        {renderGroupStats({
          courseRow,
          renderStatsTable,
          group: 'controlGroup',
        })}
        {renderBothGroupsStats({ courseRow, renderStatsTable })}
        {renderGroupComparisonStats({ courseRow, renderStatsTable })}
      </>
    );
  };

  return (
    <IdentificationStatsFactory
      stats={stats}
      isLoading={isLoading}
      groupBy={groupBy}
      getCourseTitle={getCourseTitle}
      renderCourseStats={renderCourseStats}
    />
  );
}

LevelOfImprovementObtainedStats.propTypes = {
  stats: PropTypes.object,
  isLoading: PropTypes.bool,
  groupBy: PropTypes.string,
};
